import React, { useEffect, useState } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { db } from '../firebase/config';
import {
    FaRupeeSign, FaCalendarAlt, FaChartLine, FaDownload,
    FaSpinner, FaUsers, FaCheckCircle, FaFilter,
    FaFileInvoice, FaSortAmountDown, FaSortAmountUp,
    FaEye, FaTimes, FaShoppingCart, FaUserTie
} from 'react-icons/fa';

const CommissionDistributionReport = () => {
    const [ordersWithCommissions, setOrdersWithCommissions] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [usersData, setUsersData] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');

    const [summary, setSummary] = useState({
        totalOrders: 0,
        totalCommissionAmount: 0,
        totalRecipients: 0,
        averageCommissionPerOrder: 0
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ordersPerPage = 20;

    // Fetch users data for mapping user IDs to user details
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersRef = ref(db, 'HTAMS/users');
                const snapshot = await get(usersRef);

                if (snapshot.exists()) {
                    const users = snapshot.val();
                    setUsersData(users);
                    console.log('📋 Users data loaded:', Object.keys(users).length, 'users');
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, []);

    // Fetch orders with commission data
    useEffect(() => {
        const salesRef = ref(db, 'HTAMS/salesDetails');

        const unsubscribe = onValue(salesRef, (snapshot) => {
            const data = snapshot.val();

            if (data) {
                const ordersWithCommissionsData = [];

                Object.entries(data).forEach(([orderId, orderData]) => {
                    // Only include orders where commissions have been distributed
                    if (orderData.commissionDistributed && orderData.commissions) {
                        const commissionRecipients = [];
                        let totalCommissionForOrder = 0;

                        // Extract commission recipients
                        Object.entries(orderData.commissions).forEach(([userId, commissionInfo]) => {
                            commissionRecipients.push({
                                userId,
                                amount: commissionInfo.amount || 0,
                                rate: commissionInfo.rate || 0,
                                role: commissionInfo.role || 'N/A',
                                diff: commissionInfo.diff || 0
                            });
                            totalCommissionForOrder += (commissionInfo.amount || 0);
                        });

                        ordersWithCommissionsData.push({
                            orderId: orderData.orderId || orderId,
                            customerName: orderData.customerName || 'N/A',
                            customerEmail: orderData.customerEmail || 'N/A',
                            customerPhone: orderData.customerPhone || 'N/A',
                            sellerId: orderData.sellerId || 'N/A',
                            amount: orderData.amount || 0,
                            saleDate: orderData.saleDate || orderData.timestamp || Date.now(),
                            status: orderData.status || 'N/A',
                            productName: orderData.product?.name || 'N/A',
                            quantity: orderData.quantity || 0,
                            paymentMethod: orderData.paymentMethod || 'N/A',
                            commissionRecipients,
                            totalCommission: totalCommissionForOrder,
                            recipientCount: commissionRecipients.length
                        });
                    }
                });

                console.log('📊 Orders with commissions:', ordersWithCommissionsData.length);
                setOrdersWithCommissions(ordersWithCommissionsData);
                setFilteredOrders(ordersWithCommissionsData);
                calculateSummary(ordersWithCommissionsData);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Calculate summary statistics
    const calculateSummary = (orders) => {
        const totalOrders = orders.length;
        const totalCommissionAmount = orders.reduce((sum, order) => sum + order.totalCommission, 0);
        const totalRecipients = orders.reduce((sum, order) => sum + order.recipientCount, 0);
        const averageCommissionPerOrder = totalOrders > 0 ? totalCommissionAmount / totalOrders : 0;

        console.log('📊 Summary Calculated:', {
            totalOrders,
            totalCommissionAmount,
            totalRecipients,
            averageCommissionPerOrder
        });

        setSummary({
            totalOrders,
            totalCommissionAmount,
            totalRecipients,
            averageCommissionPerOrder
        });
    };

    // Filter and sort orders
    useEffect(() => {
        let filtered = [...ordersWithCommissions];

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(order =>
                order.orderId.toLowerCase().includes(query) ||
                order.customerName.toLowerCase().includes(query) ||
                order.customerPhone.includes(query) ||
                order.productName.toLowerCase().includes(query)
            );
        }

        // Date filter
        if (dateFilter === 'custom' && startDate && endDate) {
            const start = new Date(startDate).getTime();
            const end = new Date(endDate).getTime();
            filtered = filtered.filter(order => {
                const orderDate = new Date(order.saleDate).getTime();
                return orderDate >= start && orderDate <= end;
            });
        } else if (dateFilter === 'today') {
            const today = new Date().setHours(0, 0, 0, 0);
            filtered = filtered.filter(order =>
                new Date(order.saleDate).setHours(0, 0, 0, 0) === today
            );
        } else if (dateFilter === 'week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            filtered = filtered.filter(order => new Date(order.saleDate) >= weekAgo);
        } else if (dateFilter === 'month') {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            filtered = filtered.filter(order => new Date(order.saleDate) >= monthAgo);
        }

        // Sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'date':
                    comparison = new Date(a.saleDate) - new Date(b.saleDate);
                    break;
                case 'amount':
                    comparison = a.amount - b.amount;
                    break;
                case 'commission':
                    comparison = a.totalCommission - b.totalCommission;
                    break;
                case 'recipients':
                    comparison = a.recipientCount - b.recipientCount;
                    break;
                case 'customer':
                    comparison = a.customerName.localeCompare(b.customerName);
                    break;
                default:
                    comparison = 0;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        setFilteredOrders(filtered);
        calculateSummary(filtered);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [searchQuery, dateFilter, startDate, endDate, sortBy, sortOrder, ordersWithCommissions]);

    // View order details modal
    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = [
            'Order ID', 'Order Date', 'Customer Name', 'Customer Phone', 'Product',
            'Order Amount', 'Total Commission', 'Recipients Count', 'Recipient ID',
            'Recipient Name', 'Recipient Role', 'Commission Amount', 'Commission Rate'
        ];

        const rows = [];
        filteredOrders.forEach(order => {
            order.commissionRecipients.forEach((recipient, index) => {
                const userData = usersData[recipient.userId] || {};
                rows.push([
                    index === 0 ? order.orderId : '',
                    index === 0 ? new Date(order.saleDate).toLocaleDateString() : '',
                    index === 0 ? order.customerName : '',
                    index === 0 ? order.customerPhone : '',
                    index === 0 ? order.productName : '',
                    index === 0 ? order.amount : '',
                    index === 0 ? order.totalCommission : '',
                    index === 0 ? order.recipientCount : '',
                    recipient.userId,
                    userData.name || 'N/A',
                    recipient.role,
                    recipient.amount,
                    `${recipient.rate}%`
                ]);
            });
        });

        // Add summary
        rows.push([]);
        rows.push(['SUMMARY']);
        rows.push(['Total Orders', summary.totalOrders]);
        rows.push(['Total Commission Distributed', summary.totalCommissionAmount]);
        rows.push(['Total Recipients', summary.totalRecipients]);
        rows.push(['Average Commission Per Order', summary.averageCommissionPerOrder.toFixed(2)]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `commission-distribution-report-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Print report
    const printReport = () => {
        window.print();
    };

    // Render order details modal
    const renderOrderDetailsModal = () => {
        if (!showModal || !selectedOrder) return null;

        return (
            <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content order-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Commission Distribution Details</h2>
                        <button className="close-button" onClick={closeModal}>
                            <FaTimes />
                        </button>
                    </div>
                    <div className="modal-body">
                        {/* Order Information */}
                        <div className="detail-section">
                            <h3>Order Information</h3>
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <label>Order ID</label>
                                    <p>{selectedOrder.orderId}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Order Date</label>
                                    <p>{new Date(selectedOrder.saleDate).toLocaleString()}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Customer Name</label>
                                    <p>{selectedOrder.customerName}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Customer Phone</label>
                                    <p>{selectedOrder.customerPhone}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Product</label>
                                    <p>{selectedOrder.productName} (Qty: {selectedOrder.quantity})</p>
                                </div>
                                <div className="detail-item">
                                    <label>Order Amount</label>
                                    <p>₹{selectedOrder.amount.toLocaleString()}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Payment Method</label>
                                    <p>{selectedOrder.paymentMethod}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Status</label>
                                    <span className={`status-badge ${selectedOrder.status.toLowerCase()}`}>
                                        {selectedOrder.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Seller Information */}
                        <div className="detail-section">
                            <h3>Seller Information</h3>
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <label>Seller ID</label>
                                    <p>{selectedOrder.sellerId}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Seller Name</label>
                                    <p>{usersData[selectedOrder.sellerId]?.name || 'N/A'}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Seller Phone</label>
                                    <p>{usersData[selectedOrder.sellerId]?.phone || 'N/A'}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Seller Role</label>
                                    <p>{usersData[selectedOrder.sellerId]?.role || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Commission Distribution */}
                        <div className="detail-section">
                            <h3>Commission Distribution Summary</h3>
                            <div className="commission-summary">
                                <div className="summary-stat">
                                    <span className="label">Total Commission</span>
                                    <span className="value">₹{selectedOrder.totalCommission.toLocaleString()}</span>
                                </div>
                                <div className="summary-stat">
                                    <span className="label">Recipients</span>
                                    <span className="value">{selectedOrder.recipientCount}</span>
                                </div>
                            </div>
                        </div>

                        {/* Commission Recipients Table */}
                        <div className="detail-section">
                            <h3>Commission Recipients</h3>
                            <table className="recipients-table">
                                <thead>
                                    <tr>
                                        <th>User Name</th>
                                        <th>User ID</th>
                                        <th>Role</th>
                                        <th>Rate</th>
                                        <th>Diff</th>
                                        <th>Commission Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedOrder.commissionRecipients.map((recipient, index) => {
                                        const userData = usersData[recipient.userId] || {};
                                        return (
                                            <tr key={index}>
                                                <td>{userData.name || 'N/A'}</td>
                                                <td className="user-id">{recipient.userId}</td>
                                                <td>{recipient.role}</td>
                                                <td>{recipient.rate}%</td>
                                                <td>{recipient.diff}%</td>
                                                <td className="commission-amount">₹{recipient.amount.toLocaleString()}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="commission-report-container">
                <div className="loading-container">
                    <FaSpinner className="loading-icon" />
                    <h3>Loading Commission Distribution Report...</h3>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .commission-report-container {
                    padding: 24px;
                    background: #f7fafc;
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .report-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 16px;
                    padding: 28px 32px;
                    margin-bottom: 28px;
                    color: white;
                    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .header-content {
                    flex: 1;
                    min-width: 250px;
                }

                .report-title {
                    font-size: 28px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .title-icon {
                    font-size: 32px;
                    opacity: 0.9;
                }

                .report-subtitle {
                    font-size: 15px;
                    margin: 0;
                    opacity: 0.9;
                    font-weight: 400;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .action-btn {
                    padding: 12px 24px;
                    border: none;
                    width:auto;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .export-btn {
                    background: #48bb78;
                    color: white;
                }

                .export-btn:hover {
                    background: #38a169;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(72, 187, 120, 0.4);
                }

                .print-btn {
                    background: white;
                    color: #667eea;
                }

                .print-btn:hover {
                    background: #f7fafc;
                    transform: translateY(-2px);
                }

                .view-btn {
                    background: #4299e1;
                    color: white;
                    padding: 8px 16px;
                    font-size: 13px;
                }

                .view-btn:hover {
                    background: #3182ce;
                    transform: translateY(-1px);
                }

                .summary-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                    gap: 20px;
                    margin-bottom: 28px;
                }

                .summary-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    transition: all 0.3s ease;
                    border-left: 4px solid;
                }

                .summary-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
                }

                .summary-card.total {
                    border-left-color: #667eea;
                }

                .summary-card.commission {
                    border-left-color: #48bb78;
                }

                .summary-card.recipients {
                    border-left-color: #ed8936;
                }

                .summary-card.average {
                    border-left-color: #4299e1;
                }

                .card-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    color: white;
                }

                .summary-card.total .card-icon {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .summary-card.commission .card-icon {
                    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
                }

                .summary-card.recipients .card-icon {
                    background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
                }

                .summary-card.average .card-icon {
                    background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
                }

                .card-content h3 {
                    margin: 0;
                    font-size: 28px;
                    font-weight: 700;
                    color: #2d3748 !important;
                }

                .card-content p {
                    margin: 4px 0 0 0;
                    font-size: 13px;
                    color: #718096 !important;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .filters-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px 24px;
                    margin-bottom: 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }

                .filter-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: flex-end;
                }

                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    min-width: 180px;
                }

                .filter-group.search-group {
                    flex: 1;
                    min-width: 250px;
                }

                .filter-group label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #4a5568;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .filter-select,
                .filter-input {
                    padding: 10px 14px;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 14px;
                    color: #2d3748;
                    background: white;
                    transition: all 0.2s ease;
                }

                .filter-select:focus,
                .filter-input:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .sort-toggle {
                    padding: 10px 16px;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    background: white;
                    color: #4a5568;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .sort-toggle:hover {
                    border-color: #667eea;
                    color: #667eea;
                    background: #f7fafc;
                }

                .orders-table-container {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    margin-bottom: 24px;
                }

                .orders-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .orders-table thead {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                }

                .orders-table thead th {
                    padding: 14px 14px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .orders-table thead th:nth-child(1) { width: 60px; }  /* Sr. No. */
                .orders-table thead th:nth-child(2) { width: 150px; } /* Order ID */
                .orders-table thead th:nth-child(3) { width: 100px; } /* Date */
                .orders-table thead th:nth-child(4) { width: 140px; } /* Customer */
                .orders-table thead th:nth-child(5) { width: 120px; } /* Product */
                .orders-table thead th:nth-child(6) { width: 110px; } /* Order Amount */
                .orders-table thead th:nth-child(7) { width: 110px; } /* Commission */
                .orders-table thead th:nth-child(8) { width: 90px; }  /* Recipients */
                .orders-table thead th:nth-child(9) { width: 100px; } /* Action */

                .orders-table tbody tr {
                    border-bottom: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }

                .orders-table tbody tr:hover {
                    background: #f7fafc;
                }

                .orders-table tbody td {
                    padding: 12px 14px;
                    font-size: 13px;
                    color: #2d3748;
                }

                .order-id {
                    font-family: 'Courier New', monospace;
                    font-weight: 600;
                    color: #667eea;
                    font-size: 12px;
                    max-width: 140px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    cursor: help;
                }

                .customer-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .sub-text {
                    font-size: 12px;
                    color: #718096;
                }

                .amount,
                .commission-amount {
                    font-weight: 600;
                    color: #48bb78;
                }

                .recipients-count {
                    text-align: center;
                }

                .badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 13px;
                    font-weight: 600;
                }

                /* Pagination Styles */
                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 20px;
                    padding: 20px;
                    background: #f7fafc;
                    border-top: 2px solid #e2e8f0;
                }

                .pagination-btn {
                    padding: 10px 20px;
                    border: 2px solid #667eea;
                    border-radius: 8px;
                    background: white;
                    color: #667eea;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .pagination-btn:hover:not(:disabled) {
                    background: #667eea;
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    border-color: #cbd5e0;
                    color: #a0aec0;
                }

                .pagination-info {
                    font-size: 14px;
                    font-weight: 600;
                    color: #2d3748;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }

                .pagination-total {
                    font-size: 12px;
                    color: #718096;
                    font-weight: 500;
                }

                .no-data {
                    text-align: center;
                    padding: 60px 20px !important;
                    color: #a0aec0;
                }

                .no-data-icon {
                    font-size: 48px;
                    margin-bottom: 12px;
                    opacity: 0.5;
                }

                .no-data p {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 500;
                }

                .report-footer {
                    background: white;
                    border-radius: 12px;
                    padding: 20px 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }

                .footer-summary {
                    display: flex;
                    justify-content: space-around;
                    gap: 20px;
                    flex-wrap: wrap;
                }

                .summary-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                }

                .summary-item .label {
                    font-size: 13px;
                    color: #718096;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .summary-item .value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2d3748;
                }

                .summary-item.highlight .value {
                    color: #667eea;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    backdrop-filter: blur(4px);
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    max-width: 900px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    animation: modalSlideIn 0.3s ease;
                }

                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .modal-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 24px 28px;
                    border-radius: 16px 16px 0 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 22px;
                    font-weight: 700;
                }
                  

                .close-button {
                    background: rgba(255, 255, 255, 0.2);
                    border: none;
                    color: white;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 18px;
                }

                .close-button:hover {
                    background: rgba(255, 255, 255, 0.3);
                    transform: rotate(90deg);
                }

                .modal-body {
                    padding: 28px;
                }

                .detail-section {
                    margin-bottom: 28px;
                }

                .detail-section h3 {
                    font-size: 18px;
                    font-weight: 700;
                    color: #2d3748;
                    margin: 0 0 16px 0;
                    padding-bottom: 12px;
                    border-bottom: 2px solid #e2e8f0;
                }

                .detail-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .detail-item label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #718096;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-item p {
                    margin: 0;
                    font-size: 15px;
                    color: #2d3748;
                    font-weight: 500;
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 13px;
                    font-weight: 600;
                    text-transform: capitalize;
                }

                .status-badge.completed {
                    background: #c6f6d5;
                    color: #22543d;
                }

                .status-badge.pending {
                    background: #feebc8;
                    color: #7c2d12;
                }

                .commission-summary {
                    display: flex;
                    gap: 24px;
                    flex-wrap: wrap;
                }

                .summary-stat {
                    flex: 1;
                    min-width: 180px;
                    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
                    padding: 20px;
                    border-radius: 12px;
                    border-left: 4px solid #667eea;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .summary-stat .label {
                    font-size: 13px;
                    color: #718096;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .summary-stat .value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #667eea;
                }

                .recipients-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 12px;
                }

                .recipients-table thead {
                    background: #f7fafc;
                }

                .recipients-table thead th {
                    padding: 12px 16px;
                    text-align: left;
                    font-size: 12px;
                    font-weight: 600;
                    color: #4a5568;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                }

                .recipients-table tbody tr {
                    border-bottom: 1px solid #e2e8f0;
                }

                .recipients-table tbody tr:hover {
                    background: #f7fafc;
                }

                .recipients-table tbody td {
                    padding: 14px 16px;
                    font-size: 14px;
                    color: #2d3748;
                }

                .user-id {
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                    color: #718096;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    gap: 16px;
                }

                .loading-icon {
                    font-size: 48px;
                    color: #667eea;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }

                .loading-container h3 {
                    color: #4a5568;
                    font-size: 18px;
                    font-weight: 600;
                }

                @media (max-width: 768px) {
                    .commission-report-container {
                        padding: 16px;
                    }

                    .report-header {
                        padding: 20px;
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .report-title {
                        font-size: 22px;
                    }

                    .summary-cards {
                        grid-template-columns: 1fr;
                    }

                    .filter-row {
                        flex-direction: column;
                    }

                    .filter-group {
                        width: 100%;
                        min-width: 100%;
                    }

                    .orders-table-container {
                        overflow-x: auto;
                    }

                    .orders-table {
                        min-width: 800px;
                    }

                    .modal-content {
                        margin: 10px;
                        max-width: 100%;
                    }

                    .detail-grid {
                        grid-template-columns: 1fr;
                    }

                    .recipients-table {
                        min-width: 600px;
                    }
                }

                @media print {
                    .report-header,
                    .filters-section,
                    .action-btn,
                    .header-actions {
                        display: none !important;
                    }

                    .commission-report-container {
                        background: white;
                    }

                    .summary-card {
                        box-shadow: none;
                        border: 1px solid #e2e8f0;
                    }
                }
            `}</style>

            <div className="commission-report-container">
                {/* Header */}
                <div className="report-header">
                    <div className="header-content">
                        <h1 className="report-title">
                            <FaFileInvoice className="title-icon" />
                            Commission Distribution Report
                        </h1>
                        <p className="report-subtitle">
                            Track commission distribution across all orders
                        </p>
                    </div>
                    <div className="header-actions">
                        <button className="action-btn export-btn" onClick={exportToCSV}>
                            <FaDownload /> Export CSV
                        </button>
                        <button className="action-btn print-btn" onClick={printReport}>
                            <FaFileInvoice /> Print Report
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="summary-cards">
                    <div className="summary-card total">
                        <div className="card-icon">
                            <FaShoppingCart />
                        </div>
                        <div className="card-content">
                            <h3>{summary.totalOrders}</h3>
                            <p>Total Orders</p>
                        </div>
                    </div>

                    <div className="summary-card commission">
                        <div className="card-icon">
                            <FaRupeeSign />
                        </div>
                        <div className="card-content">
                            <h3>₹{summary.totalCommissionAmount.toLocaleString()}</h3>
                            <p>Total Commission</p>
                        </div>
                    </div>

                    <div className="summary-card recipients">
                        <div className="card-icon">
                            <FaUsers />
                        </div>
                        <div className="card-content">
                            <h3>{summary.totalRecipients}</h3>
                            <p>Total Recipients</p>
                        </div>
                    </div>

                    <div className="summary-card average">
                        <div className="card-icon">
                            <FaChartLine />
                        </div>
                        <div className="card-content">
                            <h3>₹{summary.averageCommissionPerOrder.toFixed(2)}</h3>
                            <p>Avg. Commission/Order</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-section">
                    <div className="filter-row">
                        <div className="filter-group search-group">
                            <label>Search:</label>
                            <input
                                type="text"
                                placeholder="Search by Order ID, Customer, Product..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="filter-input"
                            />
                        </div>

                        <div className="filter-group">
                            <label><FaCalendarAlt /> Date Range:</label>
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Time</option>
                                <option value="today">Today</option>
                                <option value="week">Last 7 Days</option>
                                <option value="month">Last 30 Days</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {dateFilter === 'custom' && (
                            <>
                                <div className="filter-group">
                                    <label>Start Date:</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>End Date:</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>
                            </>
                        )}

                        <div className="filter-group">
                            <label><FaFilter /> Sort By:</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="filter-select"
                            >
                                <option value="date">Order Date</option>
                                <option value="amount">Order Amount</option>
                                <option value="commission">Commission Amount</option>
                                <option value="recipients">Recipients Count</option>
                                <option value="customer">Customer Name</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Order:</label>
                            <button
                                className="sort-toggle"
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            >
                                {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                                {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Orders Table */}
                <div className="orders-table-container">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>Sr. No.</th>
                                <th>Order ID</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Order Amount</th>
                                <th>Commission</th>
                                <th>Recipients</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                // Calculate pagination
                                const indexOfLastOrder = currentPage * ordersPerPage;
                                const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
                                const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

                                return currentOrders.length > 0 ? (
                                    currentOrders.map((order, index) => (
                                        <tr key={order.orderId}>
                                            <td>{indexOfFirstOrder + index + 1}</td>
                                            <td className="order-id" title={order.orderId}>
                                                {order.orderId.length > 18
                                                    ? `${order.orderId.substring(0, 18)}...`
                                                    : order.orderId}
                                            </td>
                                            <td>{new Date(order.saleDate).toLocaleDateString()}</td>
                                            <td>
                                                <div className="customer-cell">
                                                    {order.customerName}
                                                    <div className="sub-text">{order.customerPhone}</div>
                                                </div>
                                            </td>
                                            <td>{order.productName}</td>
                                            <td className="amount">₹{order.amount.toLocaleString()}</td>
                                            <td className="commission-amount">₹{order.totalCommission.toLocaleString()}</td>
                                            <td className="recipients-count">
                                                <span className="badge">{order.recipientCount}</span>
                                            </td>
                                            <td>
                                                <button className="action-btn view-btn" onClick={() => handleViewOrder(order)}>
                                                    <FaEye /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="no-data">
                                            <FaChartLine className="no-data-icon" />
                                            <p>No commission distribution records found</p>
                                        </td>
                                    </tr>
                                );
                            })()}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {filteredOrders.length > ordersPerPage && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>

                            <div className="pagination-info">
                                Page {currentPage} of {Math.ceil(filteredOrders.length / ordersPerPage)}
                                <span className="pagination-total">
                                    ({filteredOrders.length} total orders)
                                </span>
                            </div>

                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredOrders.length / ordersPerPage)))}
                                disabled={currentPage === Math.ceil(filteredOrders.length / ordersPerPage)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                {/* Summary Footer */}
                <div className="report-footer">
                    <div className="footer-summary">
                        <div className="summary-item">
                            <span className="label">Total Records:</span>
                            <span className="value">{filteredOrders.length}</span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Total Commission:</span>
                            <span className="value">₹{summary.totalCommissionAmount.toLocaleString()}</span>
                        </div>
                        <div className="summary-item highlight">
                            <span className="label">Avg. Recipients/Order:</span>
                            <span className="value">
                                {summary.totalOrders > 0 ? (summary.totalRecipients / summary.totalOrders).toFixed(1) : 0}
                            </span>
                        </div>
                    </div>
                </div>

                {renderOrderDetailsModal()}
            </div>
        </>
    );
};

export default CommissionDistributionReport;
